/**
 * slaPolicies.ts — single source of truth for SLA windows (Support, Stage 2).
 *
 * Before this lib, the SLA matrix (critical 15/2h, high 60/8h, medium 240/24h,
 * low 480/72h) was hardcoded + duplicated as a `SLA_CONFIG` constant in five
 * files, and SLA status was computed two different ways (live-from-created_at vs
 * the stored deadline columns) that could disagree. This module replaces the
 * constant with the deployed `sla_policies` table and exposes ONE set of helpers
 * that read the STORED deadline columns (support_tickets.sla_reply_deadline /
 * sla_resolve_deadline) so every display agrees.
 *
 * Division of responsibility:
 *   - CREATE paths stamp deadlines via computeDeadlines(createdAt, priority, map)
 *     — the policy map decides NEW tickets' windows.
 *   - DISPLAY/COMPUTE reads the stored deadline columns (getResolveSLA et al.) —
 *     existing tickets keep the deadline stamped at their creation (correct: an
 *     SLA is fixed at creation; changing a policy must not retroactively move it).
 *
 * Mirrors the other libs' conventions: a TABLE const, the fail() helper, and
 * fire-and-forget writeAudit on the mutation. The policy map is cached
 * module-level (fetch-once) since it is read on every support page load and
 * changes rarely; updateSlaPolicy() invalidates the cache.
 */

import { supabase } from './supabase';
import { formatDuration } from './format';
import { getCurrentWorkspaceId, requireWorkspaceId } from './apiClient';
import { writeAudit } from './audit';
import type { SupportTicket, TicketPriority } from '../types';

/** Priorities in descending urgency — the canonical order for tables/forms. */
export const SLA_PRIORITIES: readonly TicketPriority[] = ['critical', 'high', 'medium', 'low'];

/** A row of the `sla_policies` table. */
export interface SlaPolicy {
  workspace_id: string;
  priority: TicketPriority;
  first_response_mins: number;
  resolution_mins: number;
}

/** The two windows (minutes) that define a priority's SLA. */
export interface SlaWindow {
  first_response_mins: number;
  resolution_mins: number;
}

/** Policy map keyed by priority — what the rest of the app consumes. */
export type SlaPolicyMap = Record<TicketPriority, SlaWindow>;

const TABLE = 'sla_policies';

/**
 * Seeded defaults (critical 15/120, high 60/480, medium 240/1440, low 480/4320).
 * These match the deployed seed AND the old hardcoded SLA_CONFIG, so behaviour is
 * identical if the table is unreachable or a priority row is missing. Resolution
 * is in MINUTES (2h=120, 8h=480, 24h=1440, 72h=4320).
 */
export const DEFAULT_SLA_POLICY_MAP: SlaPolicyMap = {
  critical: { first_response_mins: 15,  resolution_mins: 120  },
  high:     { first_response_mins: 60,  resolution_mins: 480  },
  medium:   { first_response_mins: 240, resolution_mins: 1440 },
  low:      { first_response_mins: 480, resolution_mins: 4320 },
};

/**
 * Throw a tagged Error. Pass a PostgREST error as `from` to carry its diagnostic
 * fields through to the page — `details`/`hint` are usually the ones that name the
 * actual fix (e.g. hint: "Perhaps you meant 'campaign_id'" on a PGRST200). Plain
 * validation failures pass no `from` (or a bare code string) and are unaffected.
 */
function fail(
  message: string,
  from?: string | { code?: string; details?: string; hint?: string },
): never {
  const err = new Error(message) as Error & { code?: string; details?: string; hint?: string };
  if (typeof from === 'string') {
    err.code = from;
  } else if (from) {
    if (from.code) err.code = from.code;
    if (from.details) err.details = from.details;
    if (from.hint) err.hint = from.hint;
  }
  throw err;
}

// ─── Cache ──────────────────────────────────────────────────────────────────
// Module-level fetch-once cache. listSlaPolicies() resolves the same in-flight
// promise for concurrent callers; updateSlaPolicy() clears it so the next read
// re-fetches. Never throws to the caller — falls back to the seeded defaults.

let cachedMap: SlaPolicyMap | null = null;
let inFlight: Promise<SlaPolicyMap> | null = null;

/** Drop the cached policy map (called after a policy write). */
export function invalidateSlaPolicyCache(): void {
  cachedMap = null;
  inFlight = null;
}

async function fetchSlaPolicyMap(): Promise<SlaPolicyMap> {
  // Start from the seeded defaults so any priority absent from the table still
  // has a sane window (and the app behaves exactly as before Stage 2).
  const map: SlaPolicyMap = {
    critical: { ...DEFAULT_SLA_POLICY_MAP.critical },
    high:     { ...DEFAULT_SLA_POLICY_MAP.high },
    medium:   { ...DEFAULT_SLA_POLICY_MAP.medium },
    low:      { ...DEFAULT_SLA_POLICY_MAP.low },
  };
  try {
    // No workspace yet = keep the seeded defaults, exactly as a failed read does.
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return map;
    const { data, error } = await supabase
      .from(TABLE)
      .select('priority, first_response_mins, resolution_mins')
      .eq('workspace_id', workspaceId);
    if (error || !data) return map;
    (data as Pick<SlaPolicy, 'priority' | 'first_response_mins' | 'resolution_mins'>[]).forEach(row => {
      if (SLA_PRIORITIES.includes(row.priority)) {
        const fr = Number(row.first_response_mins);
        const res = Number(row.resolution_mins);
        map[row.priority] = {
          first_response_mins: Number.isFinite(fr) ? fr : map[row.priority].first_response_mins,
          resolution_mins:     Number.isFinite(res) ? res : map[row.priority].resolution_mins,
        };
      }
    });
    return map;
  } catch {
    return map;
  }
}

/**
 * Fetch the workspace's SLA policies as a map keyed by priority. Cached
 * (fetch-once) — repeated calls return the same map without re-querying.
 * Never throws: on any failure it resolves the seeded defaults.
 */
export async function listSlaPolicies(): Promise<SlaPolicyMap> {
  if (cachedMap) return cachedMap;
  if (inFlight) return inFlight;
  inFlight = fetchSlaPolicyMap().then(map => {
    cachedMap = map;
    inFlight = null;
    return map;
  });
  return inFlight;
}

/**
 * Update one priority's SLA window. Upserts on (workspace_id, priority) — the
 * table has a unique index there — invalidates the cache, and writes an audit
 * row. Throws (with the supabase error code) on failure.
 */
export async function updateSlaPolicy(priority: TicketPriority, window: SlaWindow): Promise<void> {
  if (!SLA_PRIORITIES.includes(priority)) fail(`Invalid priority: ${priority}`);
  const first_response_mins = Math.round(Number(window.first_response_mins));
  const resolution_mins = Math.round(Number(window.resolution_mins));
  if (!Number.isFinite(first_response_mins) || first_response_mins <= 0) {
    fail('First-response minutes must be a positive number.');
  }
  if (!Number.isFinite(resolution_mins) || resolution_mins <= 0) {
    fail('Resolution minutes must be a positive number.');
  }

  const { error } = await supabase
    .from(TABLE)
    .upsert(
      { workspace_id: requireWorkspaceId(), priority, first_response_mins, resolution_mins },
      { onConflict: 'workspace_id,priority' },
    );
  if (error) fail(error.message, error);

  invalidateSlaPolicyCache();

  void writeAudit({
    resource_type: 'sla_policies',
    action: 'updated',
    resource_id: priority,
    metadata: { priority, first_response_mins, resolution_mins },
  });
}

// ─── Deadline stamping (create paths) ─────────────────────────────────────────

/**
 * Compute the two SLA deadline timestamps for a new ticket: createdAt + the
 * priority's policy windows. Used by every ticket-create path so deadlines are
 * stamped consistently from the policy table (never the old constant).
 */
export function computeDeadlines(
  createdAt: string,
  priority: TicketPriority,
  policyMap: SlaPolicyMap,
): { sla_reply_deadline: string; sla_resolve_deadline: string } {
  const win = policyMap[priority] ?? DEFAULT_SLA_POLICY_MAP[priority];
  const base = new Date(createdAt).getTime();
  return {
    sla_reply_deadline:   new Date(base + win.first_response_mins * 60000).toISOString(),
    sla_resolve_deadline: new Date(base + win.resolution_mins * 60000).toISOString(),
  };
}

// ─── Display/compute helpers (read the STORED deadline columns) ────────────────
// One implementation, shared by all support pages, so every view agrees. These
// read support_tickets.sla_resolve_deadline (the single source of truth) — NOT a
// live recompute from created_at + a config constant.

const AT_RISK_THRESHOLD = 0.75; // ≥75% of the window elapsed = <25% time left

function isClosed(ticket: Pick<SupportTicket, 'status'>): boolean {
  return ticket.status === 'resolved' || ticket.status === 'closed';
}

/** Resolve-SLA snapshot for a ticket, derived from its stored resolve deadline. */
export interface ResolveSLA {
  /** Ticket is resolved/closed — SLA no longer ticking. */
  resolved: boolean;
  /** A stored resolve deadline is present. */
  hasDeadline: boolean;
  /** Fraction of the window elapsed (0..∞); 0 when no deadline/closed. */
  pctElapsed: number;
  /** Milliseconds until the deadline (negative once past); 0 when no deadline. */
  remainingMs: number;
  /** Past the deadline AND not resolved/closed. */
  breached: boolean;
  /** ≥75% elapsed but not yet past, and not resolved/closed. */
  atRisk: boolean;
}

/**
 * Compute the resolve-SLA state from the STORED deadline column. pctElapsed uses
 * (now − created_at) / (deadline − created_at), so it reflects the deadline that
 * was stamped on THIS ticket at creation (policy changes don't move it).
 */
export function getResolveSLA(ticket: SupportTicket): ResolveSLA {
  const resolved = isClosed(ticket);
  const deadline = ticket.sla_resolve_deadline ? new Date(ticket.sla_resolve_deadline).getTime() : null;
  if (deadline == null) {
    return { resolved, hasDeadline: false, pctElapsed: 0, remainingMs: 0, breached: false, atRisk: false };
  }
  const created = new Date(ticket.created_at).getTime();
  const now = Date.now();
  const total = deadline - created;
  const pctElapsed = total > 0 ? (now - created) / total : (now >= deadline ? Infinity : 0);
  const remainingMs = deadline - now;
  return {
    resolved,
    hasDeadline: true,
    pctElapsed,
    remainingMs,
    breached: !resolved && now > deadline,
    atRisk: !resolved && now <= deadline && pctElapsed >= AT_RISK_THRESHOLD,
  };
}

/** Fraction of the resolve window elapsed (stored-deadline driven). Sort key. */
export function slaResolvePctElapsed(ticket: SupportTicket): number {
  return getResolveSLA(ticket).pctElapsed;
}

/** True when a ticket has blown its stored resolve deadline (and isn't closed). */
export function isResolveBreached(ticket: SupportTicket): boolean {
  return getResolveSLA(ticket).breached;
}

/** True when a ticket is ≥75% through its window but not yet past it. */
export function isResolveAtRisk(ticket: SupportTicket): boolean {
  return getResolveSLA(ticket).atRisk;
}

/** Was the ticket resolved within its stored resolve deadline? (compliance %) */
export function wasResolvedInSLA(ticket: SupportTicket): boolean {
  if (!ticket.resolved_at || !ticket.sla_resolve_deadline) return false;
  return new Date(ticket.resolved_at) <= new Date(ticket.sla_resolve_deadline);
}

/**
 * Format a signed millisecond remainder as "5h 3m left" / "Breached 43d 2h ago".
 *
 * Delegates to the shared formatDuration, which ROLLS OVER INTO DAYS. This used to
 * decompose the span itself into hours+minutes with no day tier, so a ticket 43 days
 * past its deadline rendered "Breached 1034h 58m ago" — sitting six lines from the
 * same card's "43d ago". The shared formatter also rounds to whole minutes before
 * splitting, so the old "1h 60m" (from rounding 59.7m per-unit) can't happen either.
 */
export function formatSlaRemaining(remainingMs: number): string {
  const body = formatDuration(Math.abs(remainingMs));
  return remainingMs < 0 ? `Breached ${body} ago` : `${body} left`;
}
